import { api } from '../config/api';

export interface SolicitacaoAcesso {
  id: string;
  nome: string;
  email: string;
  login: string;
  senha: string;
  telefone?: string;
  perfil_id: string;
  filiais_permitidas: string[];
  cnpj?: string;
  eh_transportadora?: boolean;
  regiao?: string;
  filial_pretendida?: string;
  motivo: string;
  status: 'PENDENTE' | 'APROVADO' | 'REPROVADO';
  data_solicitacao: string;
  data_aprovacao?: string;
  aprovado_por?: string;
  motivo_reprovacao?: string;
  criado_em: string;
  atualizado_em: string;
}

export interface CriarSolicitacaoAcessoData {
  nome: string;
  email: string;
  login: string;
  senha: string;
  telefone?: string;
  perfil_id: string;
  filiais_permitidas?: string[];
  cnpj?: string;
  eh_transportadora?: boolean;
  regiao?: string;
  filial_pretendida?: string;
  motivo: string;
}

export interface AprovarSolicitacaoData {
  perfil_id: string;
  filiais_permitidas?: string[];
}

export interface ReprovarSolicitacaoData {
  motivo_reprovacao: string;
}

export interface BuscarSolicitacoesParams {
  status?: 'PENDENTE' | 'APROVADO' | 'REPROVADO';
  pagina?: number;
  limite?: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  pagina: number;
  limite: number;
}

export class SolicitacoesAcessoServiceAPI {
  // Buscar todas as solicitações
  static async buscarTodas(params?: BuscarSolicitacoesParams): Promise<PaginatedResponse<SolicitacaoAcesso>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.pagina) {
        queryParams.append('pagina', params.pagina.toString());
      }
      if (params?.limite) {
        queryParams.append('limite', params.limite.toString());
      }

      const response = await api.get(`/solicitacoes-acesso?${queryParams.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao buscar solicitações:', error);
      throw error;
    }
  }

  // Buscar solicitações pendentes
  static async buscarPendentes(pagina = 1, limite = 10): Promise<PaginatedResponse<SolicitacaoAcesso>> {
    return this.buscarTodas({ status: 'PENDENTE', pagina, limite });
  }

  // Buscar solicitação por ID
  static async buscarPorId(id: string): Promise<{ success: boolean; data: SolicitacaoAcesso }> {
    try {
      const response = await api.get(`/solicitacoes-acesso/${id}`);
      return response;
    } catch (error) {
      console.error('Erro ao buscar solicitação:', error);
      throw error;
    }
  }

  // Criar nova solicitação
  static async criar(data: CriarSolicitacaoAcessoData): Promise<{ success: boolean; message: string; data: SolicitacaoAcesso }> {
    try {
      const response = await api.post('/solicitacoes-acesso', data);
      return response;
    } catch (error) {
      console.error('Erro ao criar solicitação:', error);
      throw error;
    }
  }

  // Aprovar solicitação
  static async aprovar(id: string, data: AprovarSolicitacaoData): Promise<{ success: boolean; message: string; data: { solicitacao: SolicitacaoAcesso; usuario: any } }> {
    try {
      const response = await api.put(`/solicitacoes-acesso/${id}/aprovar`, data);
      return response;
    } catch (error) {
      console.error('Erro ao aprovar solicitação:', error);
      throw error;
    }
  }

  // Reprovar solicitação
  static async reprovar(id: string, data: ReprovarSolicitacaoData): Promise<{ success: boolean; message: string; data: SolicitacaoAcesso }> {
    try {
      const response = await api.put(`/solicitacoes-acesso/${id}/reprovar`, data);
      return response;
    } catch (error) {
      console.error('Erro ao reprovar solicitação:', error);
      throw error;
    }
  }

  // Excluir solicitação (apenas admin)
  static async excluir(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/solicitacoes-acesso/${id}`);
      return response;
    } catch (error) {
      console.error('Erro ao excluir solicitação:', error);
      throw error;
    }
  }

  // Contar solicitações por status
  static async contarPorStatus(status?: 'PENDENTE' | 'APROVADO' | 'REPROVADO'): Promise<number> {
    try {
      const response = await this.buscarTodas({ status, pagina: 1, limite: 1 });
      return response.total;
    } catch (error) {
      console.error('Erro ao contar solicitações:', error);
      return 0;
    }
  }

  // Verificar se login já existe (para validação no frontend)
  static async verificarLoginExistente(login: string): Promise<{ existe: boolean; error?: any }> {
    try {
      // Buscar usuários existentes
      const usuariosResponse = await api.get(`/usuarios?login=${encodeURIComponent(login)}&limite=1`);
      const usuarios = usuariosResponse.data || [];
      
      return { existe: usuarios.length > 0 };
    } catch (error) {
      console.error('Erro ao verificar login existente:', error);
      return { existe: false, error };
    }
  }

  // Verificar se email já existe (para validação no frontend)
  static async verificarEmailExistente(email: string): Promise<{ existe: boolean; error?: any }> {
    try {
      // Buscar usuários existentes
      const usuariosResponse = await api.get(`/usuarios?email=${encodeURIComponent(email)}&limite=1`);
      const usuarios = usuariosResponse.data || [];
      
      return { existe: usuarios.length > 0 };
    } catch (error) {
      console.error('Erro ao verificar email existente:', error);
      return { existe: false, error };
    }
  }

  // Verificar se CNPJ já existe (para validação no frontend)
  static async verificarCnpjExistente(cnpj: string): Promise<{ existe: boolean; error?: any }> {
    try {
      // Buscar usuários existentes
      const usuariosResponse = await api.get(`/usuarios?cnpj=${encodeURIComponent(cnpj)}&limite=1`);
      const usuarios = usuariosResponse.data || [];
      
      return { existe: usuarios.length > 0 };
    } catch (error) {
      console.error('Erro ao verificar CNPJ existente:', error);
      return { existe: false, error };
    }
  }

  // Verificar se login já existe em solicitações pendentes
  static async verificarLoginEmSolicitacoes(login: string): Promise<{ existe: boolean; error?: any }> {
    try {
      // Buscar solicitações pendentes com mesmo login
      const response = await this.buscarTodas({ status: 'PENDENTE', pagina: 1, limite: 100 });
      const solicitacoes = response.data || [];
      
      const existe = solicitacoes.some(s => s.login === login);
      return { existe };
    } catch (error) {
      console.error('Erro ao verificar login em solicitações:', error);
      return { existe: false, error };
    }
  }

  // Verificar se email já existe em solicitações pendentes
  static async verificarEmailEmSolicitacoes(email: string): Promise<{ existe: boolean; error?: any }> {
    try {
      // Buscar solicitações pendentes com mesmo email
      const response = await this.buscarTodas({ status: 'PENDENTE', pagina: 1, limite: 100 });
      const solicitacoes = response.data || [];
      
      const existe = solicitacoes.some(s => s.email === email);
      return { existe };
    } catch (error) {
      console.error('Erro ao verificar email em solicitações:', error);
      return { existe: false, error };
    }
  }

  // Verificar se CNPJ já existe em solicitações pendentes
  static async verificarCnpjEmSolicitacoes(cnpj: string): Promise<{ existe: boolean; error?: any }> {
    try {
      // Buscar solicitações pendentes com mesmo CNPJ
      const response = await this.buscarTodas({ status: 'PENDENTE', pagina: 1, limite: 100 });
      const solicitacoes = response.data || [];
      
      const existe = solicitacoes.some(s => s.cnpj === cnpj);
      return { existe };
    } catch (error) {
      console.error('Erro ao verificar CNPJ em solicitações:', error);
      return { existe: false, error };
    }
  }
}

export default SolicitacoesAcessoServiceAPI;
